import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Warehouse, MoreVertical, Power, Pencil, Trash2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import client, { apiErrorMessage } from '@/lib/api';
import PageHeader from '@/components/PageHeader';
import ListToolbar from '@/components/ListToolbar';
import Pagination from '@/components/PaginationBar';
import StatusBadge from '@/components/StatusBadge';
import EmptyState from '@/components/EmptyState';
import { TableSkeleton } from '@/components/Skeletons';
import ShareCredentialsModal from '@/components/ShareCredentialsModal';
import WarehouseFormDialog from '@/pages/admin/WarehouseFormDialog';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { formatDate } from '@/lib/format';

export default function WarehousesList() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [shareData, setShareData] = useState(null);

  const q = useQuery({
    queryKey: ['warehouses', { page, search, status }],
    queryFn: () => client.get('/warehouses', { params: { page, limit: 20, search: search || undefined, status: status || undefined } }).then((r) => r.data),
  });

  const items = q.data?.data || [];
  const meta = q.data?.meta?.pagination || { page: 1, totalPages: 1 };

  async function toggleStatus(w) {
    try {
      const url = w.status === 'ACTIVE' ? `/warehouses/${w.id}/disable` : `/warehouses/${w.id}/enable`;
      await client.post(url);
      toast.success(`Warehouse ${w.status === 'ACTIVE' ? 'disabled' : 'enabled'}`);
      qc.invalidateQueries({ queryKey: ['warehouses'] });
    } catch (err) { toast.error(apiErrorMessage(err)); }
  }
  async function del(w) {
    try {
      await client.delete(`/warehouses/${w.id}`);
      toast.success('Warehouse deleted');
      qc.invalidateQueries({ queryKey: ['warehouses'] });
    } catch (err) { toast.error(apiErrorMessage(err)); }
  }

  return (
    <div data-testid="admin-warehouses-page">
      <PageHeader
        title="Warehouses"
        description="Manage all tenant warehouses on the platform."
        actions={
          <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-zinc-950 hover:bg-zinc-800 text-white h-10" data-testid="wh-create-button">
            <Plus className="w-4 h-4 mr-1.5" /> New warehouse
          </Button>
        }
      />

      <ListToolbar
        search={search} onSearchChange={(v) => { setSearch(v); setPage(1); }}
        statusValue={status} statusOptions={['ACTIVE', 'DISABLED']}
        onStatusChange={(v) => { setStatus(v); setPage(1); }}
        placeholder="Search by name, email, contact…"
        testIdPrefix="wh"
      />

      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        {q.isLoading ? (
          <TableSkeleton cols={5} rows={6} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={Warehouse}
            title={search || status ? 'No matching warehouses' : 'No warehouses yet'}
            description={search || status ? 'Try adjusting your filters.' : 'Create your first warehouse to onboard a tenant.'}
            action={<Button onClick={() => setFormOpen(true)} className="bg-zinc-950 hover:bg-zinc-800 text-white"><Plus className="w-4 h-4 mr-1.5" /> New warehouse</Button>}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="warehouses-table">
              <thead>
                <tr className="bg-zinc-50 text-zinc-500 text-[11px] uppercase tracking-wider font-semibold">
                  <Th>Name</Th>
                  <Th>Contact</Th>
                  <Th>Email</Th>
                  <Th>Mobile</Th>
                  <Th className="text-right">Customers</Th>
                  <Th>Status</Th>
                  <Th>Created</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {items.map((w) => (
                  <tr key={w.id} className="hover:bg-zinc-50 transition-colors" data-testid={`wh-row-${w.id}`}>
                    <Td>
                      <Link to={`/admin/warehouses/${w.id}`} className="font-medium text-zinc-950 hover:underline">{w.name}</Link>
                      {w.freeTrialEnabled && <span className="ml-2 text-[10px] uppercase tracking-widest font-mono text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">TRIAL</span>}
                    </Td>
                    <Td className="text-zinc-700">{w.contactPersonName}</Td>
                    <Td className="font-mono text-xs text-zinc-700">{w.email}</Td>
                    <Td className="font-mono text-xs text-zinc-700">{w.mobile}</Td>
                    <Td className="text-right font-mono">{w.customerCount ?? 0}</Td>
                    <Td><StatusBadge status={w.status} testId={`wh-status-${w.id}`} /></Td>
                    <Td className="font-mono text-xs text-zinc-500">{formatDate(w.createdAt)}</Td>
                    <Td className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1.5 hover:bg-zinc-100 rounded" data-testid={`wh-actions-${w.id}`}>
                            <MoreVertical className="w-4 h-4 text-zinc-500" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuItem asChild><Link to={`/admin/warehouses/${w.id}`}><Eye className="w-4 h-4 mr-2" /> View details</Link></DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setEditing(w); setFormOpen(true); }} data-testid={`wh-edit-${w.id}`}><Pencil className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleStatus(w)} data-testid={`wh-toggle-${w.id}`}>
                            <Power className="w-4 h-4 mr-2" /> {w.status === 'ACTIVE' ? 'Disable' : 'Enable'}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <DropdownMenuItem className="text-red-600 focus:text-red-700 focus:bg-red-50" onSelect={(e) => e.preventDefault()} data-testid={`wh-delete-${w.id}`}><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete "{w.name}"?</AlertDialogTitle>
                                <AlertDialogDescription>This soft-deletes the warehouse and disables all its admin users. This action can only be reversed manually.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => del(w)} data-testid={`wh-delete-confirm-${w.id}`}>Delete</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination page={meta.page} totalPages={meta.totalPages} onChange={setPage} />
      </div>

      <WarehouseFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        initial={editing}
        onSaved={(data) => {
          setFormOpen(false);
          qc.invalidateQueries({ queryKey: ['warehouses'] });
          if (!editing && data?.credentials) {
            setShareData({
              title: 'Warehouse created',
              businessName: data.warehouse?.name,
              credentials: data.credentials,
              whatsappShareUrl: data.whatsappShareUrl,
            });
          }
        }}
      />

      <ShareCredentialsModal
        open={!!shareData}
        onClose={() => setShareData(null)}
        title={shareData?.title}
        businessName={shareData?.businessName}
        credentials={shareData?.credentials}
        whatsappShareUrl={shareData?.whatsappShareUrl}
      />
    </div>
  );
}

const Th = ({ children, className = '' }) => <th className={`text-left px-4 py-3 ${className}`}>{children}</th>;
const Td = ({ children, className = '' }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
